import { coalesce, wait, getRandomNumberBetween } from "@kazoohr/helpers";

export const MAX_RETRIES = 5;
const RETRY_DELAY_MS = 200;
// Adds random jitter to retries of between 0 and RETRY_JITTER_MS to retries
const RETRY_JITTER_MS = 1000;

/**
 * Distributed systems are complex and hard to get consistency 100% right. In
 * an effort to make our customer experience better, we really want to be able
 * to retry our queries (NOT mutations) when they fail. This fetcher inspects
 * the operation for the word "mutation". If it's NOT a mutation, this function
 * will retry the operation using the above constants as our retry strategy.
 */
// eslint-disable-next-line complexity
export const customClientFetch: typeof fetch = async (uri, options) => {
  // Requests with FormData are probably uploads. Don't mess with that :P
  if (options?.body instanceof FormData) {
    return fetch(uri, options);
  }

  let lastResponse: any;

  // The body string comes through as a stream, so convert it to a string with
  // a default.
  const bodyString = String(coalesce(options?.body, "{}"));
  const operationName = coalesce(
    JSON.parse(bodyString).operationName,
    "unknown"
  );

  /**
   * If this is a mutation we do not want to retry potentially non-idempotent mutations!
   *
   * If this is a query from API Metrics we want to avoid retrying.
   *  - API Metrics is used to query BQ. We should never be querying BQ directly in the first place
   *  - If we are, and getting failures, there is a chance BQ is overloaded already
   *  - Retrying will only further suffocate BQ resources.
   **/
  if (isMutation(bodyString) || isApiMetrics(bodyString)) {
    return fetch(uri, options);
  }

  for (let i = 0; i < MAX_RETRIES; i++) {
    const response = await fetch(uri, options);

    lastResponse = response;
    const clonedResponse = response.clone();

    let errors: string = "unknown";
    let bodyText: string = "";

    try {
      bodyText = await clonedResponse.text();
      const resultJson = JSON.parse(bodyText);

      if (resultJson.errors?.length) {
        errors = JSON.stringify(resultJson.errors);
      } else {
        break;
      }
    } catch (error) {
      errors = `${error.message}\b${error.stack}`;
    }

    // Persisted queries are handled by Apollo, so we ignore them.
    if (errors.includes("PERSISTED_QUERY_NOT_FOUND")) {
      break;
    }

    if (i !== MAX_RETRIES - 1) {
      const retryDelay =
        (RETRY_DELAY_MS + getRandomNumberBetween(0, RETRY_JITTER_MS)) * i;

      /* eslint-disable no-console */
      console.groupCollapsed(
        `Retrying graphQL fetch request after failure: ${operationName}`
      );
      console.log(`Request target: ${uri}`);
      console.log(`Response: ${response.status}`);
      console.log(`Body text: `, bodyText);
      console.log(`Errors: ${errors}`);
      console.log(`Options: `, options);
      console.log(`Retry delay: `, retryDelay);
      console.log(`Full operation: `, bodyString);
      console.log(`Retry: `, i + 1);
      console.groupEnd();
      /* eslint-enable no-console */

      await wait(retryDelay);
    }
  }

  return lastResponse;
};

function isMutation(operation: string): boolean {
  return operation.toLowerCase().includes("mutation");
}

// eslint-disable-next-line complexity
function isApiMetrics(operation: string): boolean {
  // We have access only to the query name and the query types used in the query
  // The query name in one case (for the person query) is "metrics" which I thought was too vague. I didn't want this
  // conditional to apply to any query with the word metrics in the name or type. So first we check if metrics is included
  // and then we check further for the types included
  if (!operation.includes("metrics")) {
    return false;
  }
  return (
    operation.toLowerCase().includes("teammetrics") ||
    operation.toLowerCase().includes("topcontributors") ||
    // If the operation includes metrics and the following types, we can safely assume this is a person metrics related query
    operation.toLowerCase().includes("toprecognitionidsbyhighfives") ||
    operation.toLowerCase().includes("lastreceivedfrommanager") ||
    operation.toLowerCase().includes("lastreceivedfromanyone") ||
    operation.toLowerCase().includes("totalreceived") ||
    operation.toLowerCase().includes("topcorevalue") ||
    operation.toLowerCase().includes("topskill") ||
    operation.toLowerCase().includes("totalsent") ||
    operation.toLowerCase().includes("totalreceived")
  );
}
